import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft } from "lucide-react";
import { screenWrap, screenColumn, headerRow, backIconBtn, backIconSpacer, dotsRow, dot, skipTextBtn } from "./onboardingStyles";

// ─── ONBOARDING SCREEN CHROME ──────────────────────────────────────────────────
// Shared shell for every onboarding screen: a header row (back icon, aligned
// with the progress bar) and a transition between screens. Three variants,
// picked per screen by the `anim` prop (set by whichever OnboardingFlow/
// Section2/Section3 handler triggers the navigation — see the setAnim calls
// there):
//   "slide"     — moving between screens that share the same dots (progress
//                 within one section: preview pages, Obstacles<->Discovery,
//                 Categories<->Starter Habits, steps inside the Work/School
//                 picker). No fade — a continuous filmstrip feel.
//   "fadeSlide" — entering/leaving a section with a NEW set of dots (preview
//                 -> quiz, entering/returning from the Work/School picker).
//   "none"      — no animation at all. Only the very first screen of the flow,
//                 so a new user sees it the instant the app opens.
//   "fade"      — the dot-less "Personalize your journey" transition screen
//                 only, both appearing and leaving it into Personalize —
//                 that screen has no progress dots to slide relative to, so
//                 a plain fade reads better than a directional slide.
// CSS keyframe animations rather than state-driven transitions: a keyframe
// animation starts on the element's very first paint, whereas the old
// "set entering in an effect, then settled two frames later" approach let the
// new page paint in place first and then barely nudge — it read as no
// animation at all.
const LEAVE_MS = { slide: 420, fadeSlide: 260, fade: 280, none: 0 };
const ANIM_CSS = `
@keyframes obSlideInF { from { transform: translateX(100%); } to { transform: none; } }
@keyframes obSlideInB { from { transform: translateX(-100%); } to { transform: none; } }
@keyframes obSlideOutF { from { transform: none; } to { transform: translateX(-100%); } }
@keyframes obSlideOutB { from { transform: none; } to { transform: translateX(100%); } }
@keyframes obFadeSlideInF { from { opacity: 0; transform: translateX(56px); } to { opacity: 1; transform: none; } }
@keyframes obFadeSlideInB { from { opacity: 0; transform: translateX(-56px); } to { opacity: 1; transform: none; } }
@keyframes obFadeSlideOutF { from { opacity: 1; transform: none; } to { opacity: 0; transform: translateX(-28px); } }
@keyframes obFadeSlideOutB { from { opacity: 1; transform: none; } to { opacity: 0; transform: translateX(28px); } }
@keyframes obFadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes obFadeOut { from { opacity: 1; } to { opacity: 0; } }
.ob-in-slide-forward  { animation: obSlideInF 0.42s cubic-bezier(0.4,0,0.2,1) both; }
.ob-in-slide-back     { animation: obSlideInB 0.42s cubic-bezier(0.4,0,0.2,1) both; }
.ob-out-slide-forward { animation: obSlideOutF 0.42s cubic-bezier(0.4,0,0.2,1) both; }
.ob-out-slide-back    { animation: obSlideOutB 0.42s cubic-bezier(0.4,0,0.2,1) both; }
.ob-in-fadeSlide-forward  { animation: obFadeSlideInF 0.5s cubic-bezier(0.22,1,0.36,1) 0.08s both; }
.ob-in-fadeSlide-back     { animation: obFadeSlideInB 0.5s cubic-bezier(0.22,1,0.36,1) 0.08s both; }
.ob-out-fadeSlide-forward { animation: obFadeSlideOutF 0.24s ease-in both; }
.ob-out-fadeSlide-back    { animation: obFadeSlideOutB 0.24s ease-in both; }
.ob-in-fade-forward, .ob-in-fade-back   { animation: obFadeIn 0.5s ease 0.1s both; }
.ob-out-fade-forward, .ob-out-fade-back { animation: obFadeOut 0.26s ease both; }
@media (prefers-reduced-motion: reduce) {
  [class^="ob-in-"], [class^="ob-out-"] { animation-duration: 0.01s !important; animation-delay: 0s !important; }
}
`;

const layerStyle = { display: "flex", flexDirection: "column", minHeight: 0 };

function SlideStep({ stepKey, direction, anim = "slide", children }) {
  // The page being shown, plus (briefly) the page being navigated away from,
  // so the old one can animate out while the new one animates in. The
  // outgoing page keeps its React key, so it's the same instance moved into
  // an absolutely-positioned layer — not a remount.
  const [enter, setEnter] = useState({ key: stepKey, anim, direction });
  const [leaving, setLeaving] = useState(null);
  const lastRendered = useRef({ key: stepKey, children });

  if (enter.key !== stepKey) {
    const prev = lastRendered.current;
    setLeaving({ key: prev.key, node: prev.children, anim, direction });
    setEnter({ key: stepKey, anim, direction });
  }

  useEffect(() => { lastRendered.current = { key: stepKey, children }; });

  useEffect(() => {
    if (!leaving) return;
    const t = setTimeout(() => setLeaving(null), LEAVE_MS[leaving.anim] + 60);
    return () => clearTimeout(t);
  }, [leaving]);

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, position: "relative", overflow: "hidden" }}>
      {leaving && leaving.key !== enter.key && (
        <div
          key={leaving.key}
          aria-hidden="true"
          className={`ob-out-${leaving.anim}-${leaving.direction}`}
          style={{ ...layerStyle, position: "absolute", inset: 0, pointerEvents: "none" }}
        >
          {leaving.node}
        </div>
      )}
      <div key={enter.key} className={`ob-in-${enter.anim}-${enter.direction}`} style={{ ...layerStyle, flex: 1 }}>
        {children}
      </div>
    </div>
  );
}

// progress: 1-based index of the current step; total: step count for this
// section (0 hides the dots entirely — see TransitionScreen). onBack omitted
// (undefined) hides the back icon (first screen of the whole flow, nothing
// to go back to). onSkip omitted (undefined) hides the header's Skip link —
// only the optional job-title/course screens pass it. anim: see SlideStep.
// A page's own background (the preview pages' colours) travels WITH the page
// (Jade, 2026-10-11): each colour is a full-screen layer that slides in and out
// with the same animation as the page content, so the colour changes exactly as
// the page moves — not before it (switching on tap) or after it (waiting for the
// slide to end, then fading).
function useSlidingBackground(background, dotColor, anim, direction) {
  const [layers, setLayers] = useState(() => [{ id: 0, bg: background, dot: dotColor }]);
  const top = layers[layers.length - 1];
  if (top.bg !== background || top.dot !== dotColor) {
    const id = top.id + 1;
    const dir = direction || "forward";
    setLayers([
      { ...top, cls: anim && anim !== "none" ? `ob-out-${anim}-${dir}` : undefined, leaving: true },
      { id, bg: background, dot: dotColor, cls: anim && anim !== "none" ? `ob-in-${anim}-${dir}` : undefined },
    ]);
  }
  const leaving = layers.some(l => l.leaving);
  useEffect(() => {
    if (!leaving) return undefined;
    const t = setTimeout(() => setLayers(ls => ls.filter(l => !l.leaving)), (LEAVE_MS[anim] ?? 0) + 80);
    return () => clearTimeout(t);
  }, [leaving, layers.length]); // eslint-disable-line react-hooks/exhaustive-deps
  return layers;
}

function OnboardingScreen({ onBack, progress, total, direction, stepKey, footer, onSkip, skipLabel = "Skip", anim, background, dotColor, children }) {
  const bgLayers = useSlidingBackground(background, dotColor, anim, direction);
  const anyBg = bgLayers.some(l => l.bg);
  return createPortal(
    <div className="clarity-onboarding-screen" style={screenWrap}>
      <style>{ANIM_CSS}</style>
      {anyBg && bgLayers.map(l => (
        <div key={l.id} aria-hidden="true" className={l.cls}
          style={{ position: "absolute", inset: 0, pointerEvents: "none", background: l.bg || "var(--c-hero-gradient, linear-gradient(160deg,#fff5f9,#fdeef4))" }}>
          {/* Optional faint dot grid fading down from the top (preview pages),
              the same pattern as the sign-in page. */}
          {l.dot && (
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, height: 280,
              backgroundImage: `radial-gradient(${l.dot} 1.3px, transparent 1.5px)`,
              backgroundSize: "18px 18px", opacity: 0.45,
              WebkitMaskImage: "linear-gradient(to bottom, #000, transparent)",
              maskImage: "linear-gradient(to bottom, #000, transparent)",
            }} />
          )}
        </div>
      ))}
      {/* The gradient background stays full-bleed (screenWrap above), but the
          actual header/content/footer column is capped and centered here —
          without this, every option card (optionBtn's width:100%) stretched
          edge-to-edge on wide desktop viewports instead of reading as a
          normal-width question card. */}
      <div style={{ ...screenColumn, position: "relative" }}>
        <div style={headerRow}>
          {onBack ? (
            <button onClick={onBack} style={backIconBtn} aria-label="Back">
              <ChevronLeft size={20} strokeWidth={2.5} />
            </button>
          ) : (
            <img
              src="/app-logo.png"
              alt=""
              style={{ width: 38, height: 38, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
            />
          )}
          <div style={dotsRow}>
            {Array.from({ length: Math.max(0, total) }, (_, i) => (
              <div key={i} style={dot(i < progress - 1 ? "done" : i === progress - 1 ? "current" : "upcoming")} />
            ))}
          </div>
          {onSkip ? (
            <button onClick={onSkip} style={skipTextBtn}>{skipLabel}</button>
          ) : (
            // Mirrors the left slot's width (back button / logo, both 38px)
            // so the dots row — centered via flex:1 on both sides — actually
            // lands in the middle of the screen instead of getting pulled
            // toward whichever side has content.
            <div style={backIconSpacer} />
          )}
        </div>
        <SlideStep stepKey={stepKey} direction={direction} anim={anim}>
          {children}
        </SlideStep>
        {footer}
      </div>
    </div>,
    document.body
  );
}

// The three illustrated intro pages (Jade, 2026-10-26): one strip that follows
// the finger. Swipe left = next, swipe right = back (no back button); release
// past a quarter of the screen or a quick flick turns the page, otherwise it
// springs back, and the first / last page give a little at the edge. Each
// page's colour is in a full-screen strip of its own that moves with the
// content. All drag movement is written straight to the DOM (no React render
// per frame); `index` only changes when a page actually turns.
const PAGER_EASE = "transform 0.36s cubic-bezier(0.22,1,0.36,1)";
export function PreviewPager({ pages, index, onIndexChange, onSkip, skipLabel = "Skip", footer, renderPage }) {
  const n = pages.length;
  const wrapRef = useRef(null), viewRef = useRef(null), bgRef = useRef(null), fgRef = useRef(null);
  const drag = useRef(null);
  const suppressClick = useRef(false);
  const mounted = useRef(false);

  const place = (dx, animate) => {
    const W = wrapRef.current?.clientWidth || 1;
    const C = viewRef.current?.clientWidth || W;
    for (const [el, w, d] of [[bgRef.current, W, dx], [fgRef.current, C, dx * C / W]]) {
      if (!el) continue;
      el.style.transition = animate ? PAGER_EASE : "none";
      el.style.transform = `translate3d(${-index * w + d}px,0,0)`;
    }
  };

  useLayoutEffect(() => {
    place(0, mounted.current);
    mounted.current = true;
  }, [index]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onResize = () => place(0, false);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [index]); // eslint-disable-line react-hooks/exhaustive-deps

  // Finger: touch events, so a sideways swipe can stop the browser's own
  // scrolling (pointer events get a pointercancel the moment the browser takes
  // the gesture, which turned the page by itself). Mouse: pointer events.
  const indexRef = useRef(index);
  indexRef.current = index;
  const start = (x, y, id) => {
    drag.current = { id, x0: x, y0: y, lock: null, dx: 0, px: x, pt: performance.now(), v: 0 };
  };
  // Returns true once the gesture is a sideways drag (the caller then blocks scrolling).
  const move = (x, y) => {
    const d = drag.current;
    if (!d) return false;
    const dx = x - d.x0, dy = y - d.y0;
    if (!d.lock) {
      if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) d.lock = "x";
      else if (Math.abs(dy) > 8) { drag.current = null; return false; }
      else return false;
    }
    const now = performance.now();
    d.v = (x - d.px) / Math.max(1, now - d.pt);
    d.px = x; d.pt = now;
    const i = indexRef.current;
    // Nothing before the first page or after the last: it gives a little, then springs back.
    d.dx = (i === 0 && dx > 0) || (i === n - 1 && dx < 0) ? dx * 0.35 : dx;
    place(d.dx, false);
    return true;
  };
  const end = () => {
    const d = drag.current;
    drag.current = null;
    if (!d || d.lock !== "x") return;
    suppressClick.current = true;
    setTimeout(() => { suppressClick.current = false; }, 0);
    const W = wrapRef.current?.clientWidth || 1;
    const v = performance.now() - d.pt < 120 ? d.v : 0;
    const i = indexRef.current;
    let next = i;
    if (d.dx < -W * 0.25 || v < -0.4) next = Math.min(n - 1, i + 1);
    else if (d.dx > W * 0.25 || v > 0.4) next = Math.max(0, i - 1);
    if (next !== i) onIndexChange(next);
    else place(0, true);
  };

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const ts = (e) => { if (e.touches.length === 1) start(e.touches[0].clientX, e.touches[0].clientY, "touch"); else drag.current = null; };
    const tm = (e) => { const t = e.touches[0]; if (t && move(t.clientX, t.clientY) && e.cancelable) e.preventDefault(); };
    const te = () => end();
    el.addEventListener("touchstart", ts, { passive: true });
    el.addEventListener("touchmove", tm, { passive: false });
    el.addEventListener("touchend", te);
    el.addEventListener("touchcancel", te);
    return () => {
      el.removeEventListener("touchstart", ts);
      el.removeEventListener("touchmove", tm);
      el.removeEventListener("touchend", te);
      el.removeEventListener("touchcancel", te);
    };
  }); // re-bound each render so the handlers see the latest onIndexChange

  const onPointerDown = (e) => { if (e.pointerType === "mouse" && e.button === 0) start(e.clientX, e.clientY, e.pointerId); };
  const onPointerMove = (e) => {
    if (e.pointerType !== "mouse" || drag.current?.id !== e.pointerId) return;
    const wasLocked = drag.current.lock === "x";
    if (move(e.clientX, e.clientY) && !wasLocked) { try { wrapRef.current.setPointerCapture(e.pointerId); } catch {} }
  };
  const onPointerEnd = (e) => { if (e.pointerType === "mouse" && drag.current?.id === e.pointerId) end(); };

  return createPortal(
    <div ref={wrapRef} className="clarity-onboarding-screen"
      onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerEnd} onPointerCancel={onPointerEnd}
      onClickCapture={e => { if (suppressClick.current) { e.stopPropagation(); e.preventDefault(); } }}
      style={{ ...screenWrap, userSelect: "none", WebkitUserSelect: "none" }}>
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
        <div ref={bgRef} style={{ display: "flex", width: "100%", height: "100%", willChange: "transform" }}>
          {pages.map((pg, i) => (
            <div key={i} style={{ flex: "0 0 100%", height: "100%", position: "relative", background: pg.background }}>
              {pg.accent && (
                <div style={{
                  position: "absolute", top: 0, left: 0, right: 0, height: 280,
                  backgroundImage: `radial-gradient(${pg.accent} 1.3px, transparent 1.5px)`,
                  backgroundSize: "18px 18px", opacity: 0.45,
                  WebkitMaskImage: "linear-gradient(to bottom, #000, transparent)",
                  maskImage: "linear-gradient(to bottom, #000, transparent)",
                }} />
              )}
            </div>
          ))}
        </div>
      </div>
      <div style={{ ...screenColumn, position: "relative" }}>
        <div style={headerRow}>
          <img src="/app-logo.png" alt="" draggable={false}
            style={{ width: 38, height: 38, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
          <div style={dotsRow}>
            {pages.map((_, i) => (
              <div key={i} style={dot(i < index ? "done" : i === index ? "current" : "upcoming")} />
            ))}
          </div>
          {onSkip ? <button onClick={onSkip} style={skipTextBtn}>{skipLabel}</button> : <div style={backIconSpacer} />}
        </div>
        <div ref={viewRef} style={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex" }}>
          <div ref={fgRef} style={{ display: "flex", width: "100%", minHeight: 0, willChange: "transform" }}>
            {pages.map((pg, i) => (
              <div key={i} aria-hidden={i !== index} style={{ flex: "0 0 100%", minWidth: 0, display: "flex", flexDirection: "column", minHeight: 0 }}>
                {renderPage(pg, i)}
              </div>
            ))}
          </div>
        </div>
        {footer}
      </div>
    </div>,
    document.body
  );
}

export default OnboardingScreen;
